import { PDFDocument } from "pdf-lib";

const decodeBase64ToBlob = (base64Data: string, mimeType: string): Blob => {
  let byteCharacters: string;
  try {
    byteCharacters = atob(base64Data.replace(/-/g, "+").replace(/_/g, "/"));
  } catch (error) {
    console.error("Invalid base64 data:", error);
    throw new Error("Invalid file data format", { cause: error });
  }
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
};

const decodeBase64ToUint8Array = (base64Data: string): Uint8Array => {
  let byteCharacters: string;
  try {
    byteCharacters = atob(base64Data.replace(/-/g, "+").replace(/_/g, "/"));
  } catch (error) {
    console.error("Invalid base64 data:", error);
    throw new Error("Invalid file data format", { cause: error });
  }

  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i));

  return new Uint8Array(byteNumbers);
};

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

export const mergePDF = async (...documents: string[]): Promise<string> => {
  if (documents.length === 0) {
    throw new Error("No documents provided for merging");
  }

  const mergedPdf = await PDFDocument.create();

  for (const [index, doc] of documents.entries()) {
    try {
      const docBytes = decodeBase64ToUint8Array(doc);
      const pdf = await PDFDocument.load(docBytes);
      const pageIndices = pdf.getPageIndices();
      const copiedPages = await mergedPdf.copyPages(pdf, pageIndices);

      copiedPages.forEach(page => {
        mergedPdf.addPage(page);
      });
    } catch (error) {
      console.error(`Error decoding document at index ${index}:`, error);
      throw new Error("Failed to merge one of the documents", {
        cause: error,
      });
    }
  }

  const mergedPdfBytes = await mergedPdf.save();
  return uint8ArrayToBase64(mergedPdfBytes);
};

export const printBase64File = (
  base64Data: string,
  extension: string = ".pdf"
): void => {
  const filetype =
    extension.toLowerCase().search("pdf") >= 0
      ? "application/pdf"
      : "text/plain";

  const blob = decodeBase64ToBlob(base64Data, filetype);
  const blobUrl = URL.createObjectURL(blob);
  printObjectUrl(blobUrl);
};
export const downloadObjectUrlAsFile = (
  objectUrl: string,
  filename: string,
  extension: string = ""
): void => {
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename.endsWith(extension)
    ? filename
    : `${filename}${extension}`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export const downloadBase64File = (
  base64Data: string,
  filename: string,
  extension: string = ".zpl"
): void => {
  const filetype =
    extension.toLowerCase().search("pdf") >= 0
      ? "application/pdf"
      : "application/zpl";

  const blob = decodeBase64ToBlob(base64Data, filetype);
  const blobUrl = URL.createObjectURL(blob);

  return downloadObjectUrlAsFile(blobUrl, filename, extension);
};

export const printObjectUrl = (objectUrl: string): void => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";

  const cleanup = () => {
    iframe.remove();
    URL.revokeObjectURL(objectUrl);
  };

  iframe.onload = () => {
    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      cleanup();
      return;
    }
    printWindow.addEventListener("afterprint", cleanup, { once: true });

    try {
      printWindow.print();
    } catch (error) {
      console.error("Error printing document:", error);
      cleanup();
    }
  };

  iframe.src = objectUrl;
  document.body.appendChild(iframe);
};
